import { UserRequest } from '../types';
import { Clock, CheckCircle, X, MessageSquare, Users, Eye, CheckSquare, Timer, Award } from 'lucide-react';
import { Button } from './ui/button';
import { parseDateLike } from '../utils/dateUtils';

interface UserRequestsDisplayProps {
  requests: UserRequest[];
  currentUserId: string;
  onFulfill: (requestId: string) => void;
  onDismiss: (requestId: string) => void;
}

export function UserRequestsDisplay({
  requests,
  currentUserId,
  onFulfill,
  onDismiss,
}: UserRequestsDisplayProps) {
  const pendingRequests = requests.filter(r => r.status === 'pending');
  
  // Separate requests for current user and requests made by current user
  const requestsForMe = pendingRequests.filter(r => r.requestedUser === currentUserId);
  const requestsMadeByMe = pendingRequests.filter(r => r.requestedBy === currentUserId);

  if (pendingRequests.length === 0) {
    return null;
  }

  const getRequestIcon = (type: UserRequest['requestType']) => {
    switch (type) {
      case 'comment':
        return <MessageSquare className="w-4 h-4" />;
      case 'planning_poker':
        return <Users className="w-4 h-4" />;
      case 'review':
        return <Eye className="w-4 h-4" />;
      case 'approval':
        return <CheckCircle className="w-4 h-4" />;
      case 'input':
        return <MessageSquare className="w-4 h-4" />;
      case 'estimate':
        return <Timer className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-destructive/10 text-destructive border-destructive/40';
      case 'high':
        return 'bg-chart-5/10 text-chart-5 border-chart-5/40';
      case 'low':
        return 'bg-muted/40 text-muted-foreground border-border';
      default:
        return 'bg-primary/10 text-primary border-primary/40';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = parseDateLike(dateString);
    if (!date) return 'unknown';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-4">
      {/* Requests for me */}
      {requestsForMe.length > 0 && (
        <div className="bg-chart-5/10 border-2 border-chart-5/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-5 h-5 text-chart-5" />
            <h3 className="font-semibold text-chart-5">
              Your Input Requested ({requestsForMe.length})
            </h3>
          </div>
          <div className="space-y-3">
            {requestsForMe.map(request => (
              <div
                key={request.id}
                className="bg-white border border-chart-5/40 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2">
                    {getRequestIcon(request.requestType)}
                    <span className="font-medium capitalize">
                      {request.requestType.replace('_', ' ')}
                    </span>
                    {request.priority && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(request.priority)}`}>
                        {request.priority}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {getTimeAgo(request.createdAt)}
                  </span>
                </div>
                
                <p className="text-sm text-muted-foreground mb-2">
                  <span className="font-medium">{request.requestedByName}</span> needs your {request.requestType}
                </p>
                
                {request.message && (
                  <p className="text-sm text-muted-foreground bg-muted/40 rounded p-2 mb-3">
                    "{request.message}"
                  </p>
                )}

                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => onFulfill(request.id)}
                    className="bg-chart-5/10 hover:bg-chart-5/10"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Mark as Done
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDismiss(request.id)}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Requests made by me */}
      {requestsMadeByMe.length > 0 && (
        <div className="bg-primary/10 border border-primary/40 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-primary">
              Waiting for Response ({requestsMadeByMe.length})
            </h3>
          </div>
          <div className="space-y-2">
            {requestsMadeByMe.map(request => (
              <div
                key={request.id}
                className="bg-white border border-primary/40 rounded-lg p-3 flex items-center justify-between"
              >
                <div className="flex items-center gap-2">
                  {getRequestIcon(request.requestType)}
                  <span className="text-sm">
                    Waiting for <span className="font-medium">{request.requestedUserName}</span>
                    <span className="text-muted-foreground ml-1">({request.requestType})</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{getTimeAgo(request.createdAt)}</span>
                  {request.priority === 'urgent' && (
                    <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full">
                      Urgent
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
